"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Filter, ChevronDown } from "lucide-react";
import {
  FilterModalConfig,
  FilterValues,
  FilterFieldConfig,
} from "@/types/filters";

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterValues) => void;
  config: FilterModalConfig;
  initialFilters?: FilterValues;
}

export function FilterModal({
  isOpen,
  onClose,
  onApplyFilters,
  config,
  initialFilters = {},
}: FilterModalProps) {
  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    Object.fromEntries(
      config.sections.map((section, index) => [section.title, index === 0])
    )
  );

  const toggleSection = (sectionTitle: string) => {
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[sectionTitle];
      // Close all sections first
      const allClosed = Object.fromEntries(
        config.sections.map((section) => [section.title, false])
      );
      // If the clicked section was closed, open it. If it was open, keep all closed.
      return {
        ...allClosed,
        [sectionTitle]: !isCurrentlyExpanded,
      };
    });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const handleApply = () => {
    // Remove empty values
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) => value !== "" && value !== undefined && value !== null,
      ),
    );
    onApplyFilters(cleanFilters);
    onClose();
  };

  const handleClear = () => {
    setFilters({});
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(
      (value) => value !== "" && value !== undefined && value !== null,
    ).length;
  };

  const renderField = (field: FilterFieldConfig) => {
    const value = filters[field.key] || "";
    
    switch (field.type) {
      case "select":
        return (
          <select
            value={value}
            onChange={(e) => handleFilterChange(field.key, e.target.value)}
            className="w-full p-2.5 text-xs bg-card border border-border/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
          >
            <option value="">All {field.label}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "boolean":
        return (
          <select
            value={value.toString()}
            onChange={(e) => handleFilterChange(field.key, e.target.value === "true")}
            className="w-full p-2.5 text-xs bg-card border border-border/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      case "number":
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) =>
              handleFilterChange(
                field.key,
                e.target.value ? Number(e.target.value) : "",
              )
            }
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
            className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
          />
        );

      case "date":
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleFilterChange(field.key, e.target.value)}
            className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
          />
        );

      case "string":
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleFilterChange(field.key, e.target.value)}
            placeholder={field.placeholder || `Search by ${field.label.toLowerCase()}`}
            className="bg-card border-border/60 focus:ring-primary/50 focus:border-primary text-xs h-9 shadow-sm"
          />
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card border border-border/40 shadow-2xl">
        <DialogHeader className="flex flex-row items-center justify-between pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-md">
              <Filter className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              {config.title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {config.sections.map((section) => (
            <div
              key={section.title}
              className="bg-white/80 dark:bg-background rounded-lg border border-border/40 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.title)}
                className="flex items-center justify-between w-full text-left p-3 hover:bg-blue-50/60 dark:hover:bg-primary/20 transition-colors group"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 ${section.color} rounded-full`}></div>
                  <h3 className="text-sm font-medium text-foreground">
                    {section.title}
                  </h3>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform ${
                    expandedSections[section.title] ? "rotate-180" : ""
                  }`}
                />
              </button>
              {expandedSections[section.title] && (
                <div className="p-4 bg-white/80 dark:bg-background border-t border-border/40 space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    {section.fields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-foreground mb-2">
                          {field.label}
                        </label>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border/60 bg-muted/20 rounded-lg p-4 mt-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground hover:bg-muted/60 text-sm h-8 px-3 rounded-lg"
            >
              Clear All
            </Button>
            {getActiveFilterCount() > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-sm text-muted-foreground">
                  {getActiveFilterCount()} filter
                  {getActiveFilterCount() !== 1 ? "s" : ""} active
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="text-sm h-8 px-4 border-border/60 hover:bg-muted/40 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm h-8 px-4 shadow-md rounded-lg"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default FilterModal;